import { IconExternalLink, IconUserCheck } from '@tabler/icons-react';
import { Button } from '@cfa/react-core';
import { FC } from 'react';
import styled from 'styled-components';
import { themeColors, themeSizes } from '../../styles/globalStyleVars';
import { callEvent } from '../../utils/Amplitude/AmplitudeUtilities';
import { useLocationStore } from '../../stores/locationStore';
import { TypographyStyled } from '../Styled/Typography';
import useBreakpoints from '../../hooks/useBreakpoints';
import useMediaQuery from '../../hooks/useMediaQuery';

interface Props {
  isRestaurantServiceNetwork?: boolean;
  variant?: 'default' | 'equipment-resource';
  text: { body: string; title: string };
  url: string;
}

const LinkWrapperBase = styled.div`
  align-items: center;
  color: ${themeColors.secondaryBlue};
  display: flex;
  font-weight: bold;
`;

const CardWrapper = styled.div<{
  $isEquipmentResource: boolean;
  $isMobile: boolean;
}>`
  background-color: ${themeColors.primaryWhite};
  border-radius: ${themeSizes.sm};
  width: 100%;
  padding: ${themeSizes.lg};
  ${({ $isEquipmentResource }) =>
    $isEquipmentResource && 'padding-block: 21px;'}
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ $isEquipmentResource, $isMobile }) =>
    $isEquipmentResource && !$isMobile ? themeSizes.md : themeSizes.lg};
  cursor: pointer;
  border: 1px solid ${themeColors.colorGray_2};

  &:hover {
    outline-color: ${themeColors.colorGray_3};
    border-color: ${themeColors.colorGray_3};
    transition: outline-color 0.2s ease, border-color 0.2s ease;

    ${LinkWrapperBase} {
      text-decoration: underline;
    }
  }
`;

interface LinkContentProps {
  linkLabel: string;
  isAlertsLink: boolean;
}

const LinkContent: FC<LinkContentProps> = ({ linkLabel, isAlertsLink }) => {
  const icon = (
    <IconExternalLink
      size={20}
      style={{
        marginRight: themeSizes.sm,
        textDecoration: 'none',
      }}
      aria-label='external-link-icon'
    />
  );

  if (isAlertsLink) {
    return (
      <Button
        variant='outlined'
        size='sm'
        style={{
          padding: `1px ${themeSizes.md}`,
          backgroundColor: 'white',
        }}
        id='alert-dashboard-button'
      >
        {icon}
        {linkLabel}
      </Button>
    );
  }

  return (
    <>
      {icon}
      {linkLabel}
    </>
  );
};

export const LinkWrappedCard: FC<Props> = ({
  isRestaurantServiceNetwork = false,
  variant = 'default',
  text,
  url,
}) => {
  const breakpoints = useBreakpoints();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { activeLocation } = useLocationStore();
  const isEquipmentResource = variant === 'equipment-resource';

  const isAlertsLink = !isRestaurantServiceNetwork;
  const isSplitLayout =
    (isRestaurantServiceNetwork && !isMobile) || isEquipmentResource;

  const linkLabel = isRestaurantServiceNetwork
    ? 'See details'
    : 'See real-time updates';

  const linkMarginTop = isSplitLayout ? '0' : themeSizes.md;

  const handleLinkClick = () => {
    callEvent(
      isRestaurantServiceNetwork
        ? 'launch RSN page'
        : 'launch digital performance dashboard',
      {
        locationNum: activeLocation,
      }
    );
  };

  const Link = (
    <LinkWrapperBase
      style={{
        marginTop: linkMarginTop,
      }}
    >
      <LinkContent linkLabel={linkLabel} isAlertsLink={isAlertsLink} />
    </LinkWrapperBase>
  );

  return (
    <a
      href={url}
      target='_blank'
      onClick={handleLinkClick}
      style={{ textDecoration: 'none' }}
      rel='noreferrer'
      id='performance-dashboard-link'
      aria-describedby='alert-dashboard-button'
    >
      <CardWrapper
        $isEquipmentResource={isEquipmentResource}
        $isMobile={isMobile}
      >
        <div
          style={{
            display: isSplitLayout ? 'flex' : 'block',
            justifyContent: isSplitLayout ? 'space-between' : 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          {!isEquipmentResource && (
            <div style={{ display: 'flex', marginBottom: themeSizes.smMd }}>
              <IconUserCheck color={themeColors.secondaryBlue} />
              <TypographyStyled
                text='Customer Digital services'
                color={themeColors.secondaryBlue}
                style={{
                  fontWeight: !isRestaurantServiceNetwork ? 500 : undefined,
                  marginLeft: themeSizes.sm,
                }}
              />
            </div>
          )}
          <TypographyStyled
            text={text.title}
            color={
              isEquipmentResource
                ? themeColors.colorGray_8
                : themeColors.colorBlack
            }
            fontWeight='bold'
            variant={isEquipmentResource ? 'body1' : 'h4'}
          />
          {isSplitLayout && Link}
        </div>

        <TypographyStyled
          text={text.body}
          color={
            isRestaurantServiceNetwork
              ? themeColors.colorGray_7
              : themeColors.colorBlack
          }
          style={{
            paddingTop: themeSizes.sm,
          }}
        />
        {!isSplitLayout && Link}
      </CardWrapper>
    </a>
  );
};
